import { useState, useEffect } from 'react';
import useAuthStore from '../store/authStore';
import api from '../api/offlineClient';

const ROLES_ADMIN = ['admin', 'jefe', 'administrativo'];
const UNIDADES = ['ud', 'ml', 'L', 'g', 'kg', 'm', 'm²', 'bobina', 'caja', 'rollo', 'par'];

const TIPO_LABELS = { compra: 'Compra', instalacion: 'Pedido instalación' };
const TIPO_COLORS = {
  compra:      { bg: 'rgba(74,222,128,.12)',  color: '#4ade80',  border: 'rgba(74,222,128,.3)'  },
  instalacion: { bg: 'rgba(248,113,113,.12)', color: '#f87171',  border: 'rgba(248,113,113,.3)' },
};

export default function Inventario() {
  const user = useAuthStore(s => s.user);
  if (!user || !ROLES_ADMIN.includes(user.rol)) {
    return <div style={{ padding: 40, color: 'var(--text-dim)', textAlign: 'center' }}>Sin acceso.</div>;
  }
  return <InventarioAdmin />;
}

function InventarioAdmin() {
  const [tab, setTab] = useState('materiales');
  const [materiales, setMateriales] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [loadingMat, setLoadingMat] = useState(false);
  const [loadingMov, setLoadingMov] = useState(false);
  const [editItem, setEditItem] = useState(null); // null | {} para crear | objeto para editar
  const [movForm, setMovForm] = useState({ tipo: 'compra', items: [{ material_id: '', cantidad: '' }], referencia: '', notas: '' });
  const [saving, setSaving] = useState(false);

  function loadMateriales() {
    setLoadingMat(true);
    api.get('/api/inventario/materiales')
      .then(r => setMateriales(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingMat(false));
  }

  function loadMovimientos() {
    setLoadingMov(true);
    api.get('/api/inventario/movimientos')
      .then(r => setMovimientos(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingMov(false));
  }

  useEffect(() => { loadMateriales(); }, []);
  useEffect(() => { if (tab === 'historial') loadMovimientos(); }, [tab]);

  async function saveMaterial(data) {
    setSaving(true);
    try {
      if (data.id) {
        const r = await api.put(`/api/inventario/materiales/${data.id}`, data);
        setMateriales(prev => prev.map(m => m.id === data.id ? r.data : m));
      } else {
        const r = await api.post('/api/inventario/materiales', data);
        setMateriales(prev => [...prev, r.data]);
      }
      setEditItem(null);
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  }

  async function deleteMaterial(id) {
    if (!window.confirm('¿Archivar este material? Dejará de aparecer en el inventario activo.')) return;
    try {
      await api.delete(`/api/inventario/materiales/${id}`);
      setMateriales(prev => prev.filter(m => m.id !== id));
    } catch (e) { alert('Error: ' + e.message); }
  }

  async function saveMovimiento() {
    const { tipo, items, referencia, notas } = movForm;
    const valid = items.filter(it => it.material_id && it.cantidad);
    if (!valid.length) { alert('Añade al menos un artículo con cantidad'); return; }
    setSaving(true);
    try {
      for (const it of valid) {
        await api.post('/api/inventario/movimientos', { tipo, material_id: parseInt(it.material_id), cantidad: parseFloat(it.cantidad), referencia, notas });
      }
      loadMateriales();
      if (tab === 'historial') loadMovimientos();
      setMovForm({ tipo: 'compra', items: [{ material_id: '', cantidad: '' }], referencia: '', notas: '' });
      alert(`${valid.length} artículo${valid.length > 1 ? 's' : ''} registrado${valid.length > 1 ? 's' : ''} correctamente.`);
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  }

  function setMovItem(idx, field, value) {
    setMovForm(p => {
      const items = p.items.map((it, i) => i === idx ? { ...it, [field]: value } : it);
      return { ...p, items };
    });
  }

  function addMovItem() {
    setMovForm(p => ({ ...p, items: [...p.items, { material_id: '', cantidad: '' }] }));
  }

  function removeMovItem(idx) {
    setMovForm(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));
  }

  const activeMats = materiales.filter(m => m.activo !== false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, padding: '8px 12px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        {[
          { key: 'materiales', label: '📦 Materiales' },
          { key: 'movimiento', label: '➕ Nuevo movimiento' },
          { key: 'historial',  label: '🗂️ Historial' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '7px 16px', borderRadius: 6, fontWeight: 700, fontSize: 12,
            cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
            background: tab === t.key ? 'var(--amber)' : 'var(--surface2)',
            color:      tab === t.key ? '#000' : 'var(--text-dim)',
          }}>{t.label}</button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {/* ── Materiales ── */}
        {tab === 'materiales' && (
          <div style={{ maxWidth: 860, margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                {activeMats.length} material{activeMats.length !== 1 ? 'es' : ''}
              </span>
              <button onClick={() => setEditItem({})} style={BTN_ADD}>+ Añadir material</button>
            </div>

            {loadingMat ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-dim)' }}>Cargando…</div>
            ) : activeMats.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-dim)' }}>
                Sin materiales. Usa "+ Añadir material" para comenzar.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Material', 'Unidad', 'Precio (€)', 'Stock actual', 'Stock mínimo', 'Descripción', ''].map(h => (
                        <th key={h} style={{ ...TH, textAlign: h === '' ? 'center' : 'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activeMats.map((m, i) => {
                      const bajo = parseFloat(m.stock_actual) <= parseFloat(m.stock_minimo) && parseFloat(m.stock_minimo) > 0;
                      return (
                        <tr key={m.id} style={{ background: i % 2 === 0 ? 'var(--surface-deep)' : 'var(--surface)' }}>
                          <td style={TD}>
                            <span style={{ fontWeight: 700, color: 'var(--text)', fontSize: 13 }}>{m.nombre}</span>
                          </td>
                          <td style={{ ...TD, color: 'var(--text-dim)', fontSize: 12 }}>{m.unidad}</td>
                          <td style={{ ...TD, fontSize: 13, fontWeight: 700, color: 'var(--amber)' }}>
                            {parseFloat(m.precio) > 0 ? parseFloat(m.precio).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' : '—'}
                          </td>
                          <td style={TD}>
                            <span style={{
                              fontWeight: 800, fontSize: 14,
                              color: bajo ? '#f87171' : parseFloat(m.stock_actual) > 0 ? '#4ade80' : 'var(--text-dim)',
                            }}>
                              {parseFloat(m.stock_actual).toLocaleString('es-ES', { maximumFractionDigits: 2 })}
                            </span>
                            {bajo && <span style={{ fontSize: 9, color: '#f87171', marginLeft: 5, fontWeight: 800 }}>▼ BAJO</span>}
                          </td>
                          <td style={{ ...TD, color: 'var(--text-dim)', fontSize: 12 }}>
                            {parseFloat(m.stock_minimo) > 0 ? parseFloat(m.stock_minimo).toLocaleString('es-ES', { maximumFractionDigits: 2 }) : '—'}
                          </td>
                          <td style={{ ...TD, color: 'var(--text-dim)', fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {m.descripcion || '—'}
                          </td>
                          <td style={{ ...TD, textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 5, justifyContent: 'center' }}>
                              <button onClick={() => setEditItem(m)} style={BTN_EDIT}>✎</button>
                              <button onClick={() => deleteMaterial(m.id)} style={BTN_DEL}>🗑</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Nuevo movimiento ── */}
        {tab === 'movimiento' && (
          <div style={{ maxWidth: 540, margin: '0 auto' }}>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px 22px' }}>
              <div style={{ fontSize: 11, color: 'var(--amber)', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 18 }}>
                Registrar movimiento
              </div>

              {/* Tipo */}
              <div style={{ marginBottom: 14 }}>
                <label style={LBL}>Tipo</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['compra', 'instalacion'].map(t => (
                    <button key={t} onClick={() => setMovForm(p => ({ ...p, tipo: t }))} style={{
                      flex: 1, padding: '9px 12px', borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: 'pointer',
                      border: movForm.tipo === t ? 'none' : '1px solid var(--border)',
                      background: movForm.tipo === t
                        ? (t === 'compra' ? 'rgba(74,222,128,.2)' : 'rgba(248,113,113,.2)')
                        : 'var(--surface2)',
                      color: movForm.tipo === t
                        ? (t === 'compra' ? '#4ade80' : '#f87171')
                        : 'var(--text-dim)',
                    }}>
                      {t === 'compra' ? '📥 Compra (suma stock)' : '📤 Pedido instalación (resta stock)'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Lista de artículos */}
              <div style={{ marginBottom: 14 }}>
                <label style={LBL}>Artículos</label>
                <div style={{ display: 'grid', gap: 7 }}>
                  {movForm.items.map((it, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 90px auto', gap: 7, alignItems: 'center' }}>
                      <select
                        value={it.material_id}
                        onChange={e => setMovItem(idx, 'material_id', e.target.value)}
                        style={SEL}
                      >
                        <option value="">— Artículo —</option>
                        {activeMats.map(m => (
                          <option key={m.id} value={m.id}>
                            {m.nombre} ({m.unidad})
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={it.cantidad}
                        onChange={e => setMovItem(idx, 'cantidad', e.target.value)}
                        placeholder="Cant."
                        style={{ ...INP, textAlign: 'center' }}
                      />
                      <button
                        onClick={() => removeMovItem(idx)}
                        disabled={movForm.items.length === 1}
                        style={{ background: movForm.items.length === 1 ? 'var(--surface2)' : 'rgba(248,113,113,.15)', border: `1px solid ${movForm.items.length === 1 ? 'var(--border)' : 'rgba(248,113,113,.4)'}`, color: movForm.items.length === 1 ? 'var(--text-dim)' : '#f87171', borderRadius: 6, padding: '8px 10px', cursor: movForm.items.length === 1 ? 'default' : 'pointer', fontWeight: 800, fontSize: 14 }}
                      >×</button>
                    </div>
                  ))}
                </div>
                <button onClick={addMovItem} style={{ marginTop: 7, background: 'transparent', border: '1.5px dashed var(--border)', color: 'var(--text-dim)', padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, width: '100%' }}>
                  + Añadir artículo
                </button>
              </div>

              {/* Referencia */}
              <div style={{ marginBottom: 14 }}>
                <label style={LBL}>{movForm.tipo === 'compra' ? 'Nº Pedido / Referencia' : 'Instalación / Referencia'}</label>
                <input
                  type="text"
                  value={movForm.referencia}
                  onChange={e => setMovForm(p => ({ ...p, referencia: e.target.value }))}
                  placeholder={movForm.tipo === 'compra' ? 'PED-2026-001' : 'Nombre instalación o número'}
                  style={INP}
                />
              </div>

              {/* Notas */}
              <div style={{ marginBottom: 20 }}>
                <label style={LBL}>Notas (opcional)</label>
                <textarea
                  value={movForm.notas}
                  onChange={e => setMovForm(p => ({ ...p, notas: e.target.value }))}
                  rows={2}
                  style={{ ...INP, resize: 'vertical' }}
                />
              </div>

              <button onClick={saveMovimiento} disabled={saving} style={{
                width: '100%', padding: '11px', borderRadius: 8, fontWeight: 800, fontSize: 13, cursor: 'pointer',
                background: movForm.tipo === 'compra' ? 'rgba(74,222,128,.15)' : 'rgba(248,113,113,.15)',
                color: movForm.tipo === 'compra' ? '#4ade80' : '#f87171',
                border: `1.5px solid ${movForm.tipo === 'compra' ? 'rgba(74,222,128,.4)' : 'rgba(248,113,113,.4)'}`,
                opacity: saving ? 0.6 : 1,
              }}>
                {saving ? 'Guardando…' : movForm.tipo === 'compra' ? '📥 Registrar compra' : '📤 Registrar pedido'}
              </button>
            </div>
          </div>
        )}

        {/* ── Historial ── */}
        {tab === 'historial' && (
          <div style={{ maxWidth: 860, margin: '0 auto' }}>
            {loadingMov ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-dim)' }}>Cargando…</div>
            ) : movimientos.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-dim)' }}>Sin movimientos registrados.</div>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {movimientos.map(m => {
                  const col = TIPO_COLORS[m.tipo] || TIPO_COLORS.compra;
                  return (
                    <div key={m.id} style={{
                      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
                      padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                    }}>
                      <span style={{
                        fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap',
                        letterSpacing: '.05em', background: col.bg, color: col.color, border: `1px solid ${col.border}`,
                      }}>
                        {TIPO_LABELS[m.tipo] || m.tipo}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                          {m.material_nombre}
                          <span style={{ marginLeft: 6, fontSize: 11, color: m.tipo === 'compra' ? '#4ade80' : '#f87171', fontWeight: 800 }}>
                            {m.tipo === 'compra' ? '+' : '−'}{parseFloat(m.cantidad).toLocaleString('es-ES', { maximumFractionDigits: 2 })} {m.unidad}
                          </span>
                        </div>
                        {m.referencia && (
                          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 1 }}>{m.referencia}</div>
                        )}
                        {m.notas && (
                          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 1, fontStyle: 'italic' }}>{m.notas}</div>
                        )}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-dim)', whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {m.created_by_nombre && <div style={{ fontWeight: 600 }}>{m.created_by_nombre}</div>}
                        <div>{new Date(m.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal edición material */}
      {editItem !== null && (
        <MaterialModal
          item={editItem}
          onSave={saveMaterial}
          onClose={() => setEditItem(null)}
          saving={saving}
        />
      )}
    </div>
  );
}

function MaterialModal({ item, onSave, onClose, saving }) {
  const isNew = !item.id;
  const [form, setForm] = useState({
    nombre: item.nombre || '',
    unidad: item.unidad || 'ud',
    precio: item.precio != null ? String(item.precio) : '0',
    stock_actual: item.stock_actual != null ? String(item.stock_actual) : '0',
    stock_minimo: item.stock_minimo != null ? String(item.stock_minimo) : '0',
    descripcion: item.descripcion || '',
  });

  function fld(k) { return { value: form[k], onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) }; }

  function handleSave() {
    if (!form.nombre.trim()) { alert('El nombre es obligatorio'); return; }
    onSave({
      ...item,
      nombre: form.nombre.trim(),
      unidad: form.unidad,
      precio: parseFloat(form.precio) || 0,
      stock_actual: parseFloat(form.stock_actual) || 0,
      stock_minimo: parseFloat(form.stock_minimo) || 0,
      descripcion: form.descripcion || null,
    });
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, maxWidth: 440, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--amber)' }}>
            {isNew ? 'Nuevo material' : 'Editar material'}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={LBL}>Nombre *</label>
          <input type="text" {...fld('nombre')} placeholder="Ej: Detector de humo" style={INP} autoFocus />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <label style={LBL}>Unidad</label>
            <select {...fld('unidad')} style={SEL}>
              {UNIDADES.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <label style={LBL}>Precio (€)</label>
            <input type="number" min="0" step="0.01" {...fld('precio')} placeholder="0.00" style={INP} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <label style={LBL}>Stock actual</label>
            <input type="number" min="0" step="0.5" {...fld('stock_actual')} style={INP} />
          </div>
          <div>
            <label style={LBL}>Stock mínimo</label>
            <input type="number" min="0" step="0.5" {...fld('stock_minimo')} placeholder="0" style={INP} />
          </div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={LBL}>Descripción (opcional)</label>
          <textarea rows={2} {...fld('descripcion')} placeholder="Referencia, modelo, notas…" style={{ ...INP, resize: 'vertical' }} />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ ...BTN_SEC, flex: 1 }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} style={{ ...BTN_PRIM, flex: 2, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Guardando…' : isNew ? 'Crear material' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

const LBL  = { fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', display: 'block', marginBottom: 4 };
const INP  = { background: 'var(--surface-deep)', border: '1.5px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '8px 10px', width: '100%', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
const SEL  = { background: 'var(--surface-deep)', border: '1.5px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '8px 10px', width: '100%', fontSize: 13, fontFamily: 'inherit' };
const TH   = { background: 'var(--surface)', border: '1px solid var(--border)', padding: '9px 12px', fontSize: 11, fontWeight: 800, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '.05em' };
const TD   = { border: '1px solid var(--border)', padding: '9px 12px', verticalAlign: 'middle' };
const BTN_ADD  = { background: 'rgba(245,158,11,.12)', border: '1.5px solid rgba(245,158,11,.4)', color: 'var(--amber)', padding: '7px 16px', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 12 };
const BTN_EDIT = { background: 'rgba(59,130,246,.12)', border: '1px solid rgba(59,130,246,.4)', color: '#60a5fa', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13, lineHeight: 1 };
const BTN_DEL  = { background: 'rgba(248,113,113,.12)', border: '1px solid rgba(248,113,113,.4)', color: '#f87171', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13, lineHeight: 1 };
const BTN_PRIM = { background: 'var(--amber)', color: '#000', border: 'none', padding: '10px 18px', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 13 };
const BTN_SEC  = { background: 'var(--surface2)', color: 'var(--text-dim)', border: '1px solid var(--border)', padding: '10px 14px', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: 12 };
